import { useState } from 'react';
import { Alert, App, Button, Input, Space, Tag, Typography } from 'antd';

import {
	clearAttestationPin,
	disableMfa,
	enableMfa,
	regenerateRecoveryCodes,
	setAttestationPin,
	startMfaSetup,
} from '../../services/mfa';
import ConfirmWordModal from '../common/ConfirmWordModal';

/**
 * The account's two security settings, side by side because people confuse
 * them and the difference is the whole point:
 *
 *   Two-step sign-in   is this ACCOUNT really them, once, at sign-in
 *   Signing PIN        who performed THIS act, every time, on a shared device
 *
 * No QR library. The setup shows the secret and the `otpauth://` link, which
 * every authenticator accepts by paste or tap — a dependency whose only job
 * is drawing squares is not worth carrying, and the text form is the one
 * that works when the camera is the problem.
 */

const { Text, Paragraph } = Typography;

export default function AccountProtectionSection({ user, onChanged }) {

	const { message } = App.useApp();
	const mfaOn = !!user?.mfa?.enabled;
	const hasPin = !!user?.hasAttestationPin;

	const [ setup, setSetup ] = useState(null);       // { secret, otpauth }
	const [ code, setCode ] = useState('');
	const [ codes, setCodes ] = useState(null);       // shown once
	const [ busy, setBusy ] = useState(false);

	const [ pin, setPin ] = useState('');
	const [ pinPassword, setPinPassword ] = useState('');

	// Turning two-step sign-in off. The password manager may fill the password
	// and the code (utils/fieldAssist.js), so the person still TYPES the word
	// before the button arms — the same guard as deleting the account.
	const [ offOpen, setOffOpen ] = useState(false);
	const [ offPassword, setOffPassword ] = useState('');
	const [ offCode, setOffCode ] = useState('');
	const [ offBusy, setOffBusy ] = useState(false);

	const begin = async () => {
		setBusy(true);
		try {
			setSetup(await startMfaSetup());
			setCode('');
		}
		catch (err) { message.error(err?.response?.data?.message || 'Could not start setup.'); }
		finally { setBusy(false); }
	};

	const finish = async () => {
		setBusy(true);
		try {
			const result = await enableMfa(code);
			setCodes(result.recoveryCodes);
			setSetup(null);
			onChanged?.();
			message.success('Two-step sign-in is on.');
		}
		catch (err) { message.error(err?.response?.data?.message || 'That code is not right.'); }
		finally { setBusy(false); }
	};

	const openTurnOff = () => {
		setOffPassword('');
		setOffCode('');
		setOffOpen(true);
	};

	const turnOff = async () => {
		setOffBusy(true);
		try {
			await disableMfa({ password: offPassword, code: offCode });
			setOffOpen(false);
			onChanged?.();
			message.success('Two-step sign-in is off.');
		}
		catch (err) { message.error(err?.response?.data?.message || 'That password or code is not right.'); }
		finally { setOffBusy(false); }
	};

	const rotate = async () => {
		setBusy(true);
		try {
			const result = await regenerateRecoveryCodes(code);
			setCodes(result.recoveryCodes);
			setCode('');
		}
		catch (err) { message.error(err?.response?.data?.message || 'That code is not right.'); }
		finally { setBusy(false); }
	};

	const savePin = async () => {
		setBusy(true);
		try {
			await setAttestationPin({ pin, password: pinPassword });
			setPin('');
			setPinPassword('');
			onChanged?.();
			message.success('Your signing PIN is set.');
		}
		catch (err) { message.error(err?.response?.data?.message || 'Could not set that PIN.'); }
		finally { setBusy(false); }
	};

	return (
		<section className='form-panel security-section'>
			<div className='micro-label form-panel__kicker'>Protecting your account</div>

			<div className='security-section__block'>
				<Space align='center' wrap>
					<Text strong>Two-step sign-in</Text>
					<Tag color={mfaOn ? 'green' : 'default'}>{mfaOn ? 'On' : 'Off'}</Tag>
				</Space>
				<Paragraph type='secondary'>
					A code from your phone as well as your password. Without it, anyone who learns your
					password is you.
				</Paragraph>

				{!mfaOn && !setup ? (
					<Button onClick={begin} loading={busy}>Set up</Button>
				) : null}

				{setup ? (
					<Space direction='vertical' style={{ width: '100%' }}>
						<Alert
							type='info'
							showIcon
							message='Add this to your authenticator'
							description={(
								<Space direction='vertical' style={{ width: '100%' }}>
									<Text code copyable>{setup.secret}</Text>
									<a href={setup.otpauth}>Open in an authenticator app</a>
									<Text type='secondary'>Then type the six digits it shows.</Text>
								</Space>
							)}
						/>
						<Input
							style={{ maxWidth: 200 }}
							value={code}
							inputMode='numeric'
							maxLength={6}
							autoComplete='one-time-code'
							aria-label='Code from your authenticator'
							placeholder='000000'
							onChange={event => setCode(event.target.value.replace(/\D/g, ''))}
						/>
						<Space>
							<Button onClick={() => setSetup(null)}>Cancel</Button>
							<Button type='primary' onClick={finish} loading={busy} disabled={code.length !== 6}>Turn it on</Button>
						</Space>
					</Space>
				) : null}

				{mfaOn ? (
					<Space wrap>
						<Input
							style={{ width: 160 }}
							value={code}
							inputMode='numeric'
							maxLength={6}
							autoComplete='one-time-code'
							aria-label='Current code'
							placeholder='Current code'
							onChange={event => setCode(event.target.value.replace(/\D/g, ''))}
						/>
						<Button onClick={rotate} loading={busy} disabled={code.length !== 6}>New recovery codes</Button>
						<Button danger onClick={openTurnOff}>Turn off…</Button>
					</Space>
				) : null}

				<ConfirmWordModal
					title='Turn off two-step sign-in?'
					open={offOpen}
					word='turn off'
					okText='Turn off'
					onOk={turnOff}
					onCancel={() => setOffOpen(false)}
					extraValid={Boolean(offPassword) && offCode.length === 6}
					busy={offBusy}
				>
					<p>Your password and a current code, because turning protection off is exactly when a stolen session would like to act.</p>
					<Space direction='vertical' style={{ width: '100%', marginBottom: 12 }}>
						<Input.Password
							value={offPassword}
							autoComplete='current-password'
							aria-label='Your password'
							placeholder='Your password'
							onChange={event => setOffPassword(event.target.value)}
						/>
						<Input
							value={offCode}
							inputMode='numeric'
							maxLength={6}
							autoComplete='one-time-code'
							aria-label='Code from your authenticator'
							placeholder='Code from your authenticator'
							onChange={event => setOffCode(event.target.value.replace(/\D/g, ''))}
						/>
					</Space>
				</ConfirmWordModal>

				{codes ? (
					<Alert
						style={{ marginTop: 12 }}
						type='warning'
						showIcon
						message='Save these recovery codes now'
						description={(
							<Space direction='vertical'>
								<Text type='secondary'>Each works once, if you lose your phone. They are not shown again — we keep only hashes of them.</Text>
								<Text code copyable={{ text: codes.join('\n') }}>{codes.join('  ')}</Text>
								<Button size='small' onClick={() => setCodes(null)}>I have saved them</Button>
							</Space>
						)}
					/>
				) : null}
			</div>

			<div className='security-section__block'>
				<Space align='center' wrap>
					<Text strong>Signing PIN</Text>
					<Tag color={hasPin ? 'green' : 'default'}>{hasPin ? 'Set' : 'Not set'}</Tag>
				</Space>
				<Paragraph type='secondary'>
					Four to eight digits you type when you sign a record. It proves who performed
					that entry on a device several people share — it can never sign you in.
				</Paragraph>
				<Space wrap align='start'>
					<Input
						style={{ width: 140 }}
						value={pin}
						inputMode='numeric'
						maxLength={8}
						aria-label='New PIN'
						placeholder='New PIN'
						onChange={event => setPin(event.target.value.replace(/\D/g, ''))}
					/>
					<Input.Password
						style={{ width: 220 }}
						value={pinPassword}
						autoComplete='current-password'
						aria-label='Your password'
						placeholder='Your password'
						onChange={event => setPinPassword(event.target.value)}
					/>
					<Button onClick={savePin} loading={busy} disabled={pin.length < 4 || !pinPassword}>
						{hasPin ? 'Change PIN' : 'Set PIN'}
					</Button>
					{hasPin ? (
						<Button
							danger
							onClick={async () => {
								await clearAttestationPin();
								onChanged?.();
								message.success('PIN removed. Your password still signs records.');
							}}
						>
							Remove
						</Button>
					) : null}
				</Space>
			</div>
		</section>
	);
}
